import { IBus }                                             from '@/Emulator/Bus';
import { NesPalette }                                       from '@/Emulator/Hardware/PPU/NesPalette';
import { ControlRegister }                                  from '@/Emulator/Hardware/PPU/Register/ControlRegister';
import { MaskRegister }                                     from '@/Emulator/Hardware/PPU/Register/MaskRegister';
import { StatusRegister }                                                      from '@/Emulator/Hardware/PPU/Register/StatusRegister';
import { deserializeMemory, Register, Register16, Register8, serializeMemory } from '@/Emulator/Utility';
import { EventDispatcher }                                                     from '@/Event';

/**
 * The Picture Processing Unit.
 */
export class PPU extends EventDispatcher<IPPUEvents> implements ISerializable
{
    public readonly frameBuffer: Uint32Array = new Uint32Array(256 * 240);
    public readonly oamMemory: Uint8Array    = new Uint8Array(256);
    public readonly secondaryOam: Uint8Array = new Uint8Array(32);

    public readonly ctrlRegister: ControlRegister  = new ControlRegister();
    public readonly maskRegister: MaskRegister     = new MaskRegister();
    public readonly statusRegister: StatusRegister = new StatusRegister();

    public oamAddress: number = 0x00; // FIXME: This mutated from CPUBus. Eww.

    private readonly nametableRegister: Register8           = Register.make8();
    private readonly attributeTableLowRegister: Register16  = Register.make16();
    private readonly attributeTableHighRegister: Register16 = Register.make16();
    private readonly patternTableLowRegister: Register16    = Register.make16();
    private readonly patternTableHighRegister: Register16   = Register.make16();

    private readonly spritePatternLow: Uint8Array      = new Uint8Array(8);
    private readonly spritePatternHigh: Uint8Array     = new Uint8Array(8);
    private readonly spriteAttributes: Uint8Array      = new Uint8Array(8);
    private readonly spriteXCounters: Uint8Array       = new Uint8Array(8);
    private readonly nextSpritePatternLow: Uint8Array  = new Uint8Array(8);
    private readonly nextSpritePatternHigh: Uint8Array = new Uint8Array(8);
    private readonly nextSpriteAttributes: Uint8Array  = new Uint8Array(8);
    private readonly nextSpriteXCounters: Uint8Array   = new Uint8Array(8);

    private _spriteCount: number                   = 0;
    private _nextSpriteCount: number               = 0;
    private _spriteZeroInSecondaryOam: boolean     = false;
    private _nextSpriteZeroInSecondaryOam: boolean = false;
    private _addressLatch: boolean                 = false;
    private _nametableLatch: number                = 0;
    private _attributeTableLowLatch: number        = 0;
    private _attributeTableHighLatch: number       = 0;
    private _patternTableLowLatch: number          = 0;
    private _patternTableHighLatch: number         = 0;
    private _readBuffer: number                    = 0;

    private _vramAddress: number = 0;
    private _tempAddress: number = 0;
    private _fineXScroll: number = 0;
    private _frame: number       = 0;
    private _scanline: number    = 0;
    private _cycle: number       = 0;

    constructor(private readonly bus: IBus)
    {
        super();
    }

    public serialize(): Record<string, any>
    {
        return {
            // Memory
            oamMemory:    serializeMemory(this.oamMemory),
            secondaryOam: serializeMemory(this.secondaryOam),

            // Registers
            ctrl:       this.ctrlRegister.value,
            mask:       this.maskRegister.value,
            status:     this.statusRegister.value,
            oamAddress: this.oamAddress,
            readBuffer: this._readBuffer,

            // Loopy (Scroll) Registers
            vramAddress:  this._vramAddress,
            tempAddress:  this._tempAddress,
            fineXScroll:  this._fineXScroll,
            addressLatch: this._addressLatch,

            // Timing
            cycle:    this._cycle,
            scanline: this._scanline,
            frame:    this._frame,

            // Mid-scanline Latches (Arrays converted to standard JS arrays for JSON)
            spriteCount:                  this._spriteCount,
            nextSpriteCount:              this._nextSpriteCount,
            spriteZeroInSecondaryOam:     this._spriteZeroInSecondaryOam,
            nextSpriteZeroInSecondaryOam: this._nextSpriteZeroInSecondaryOam,
            spritePatternLow:             Array.from(this.spritePatternLow),
            spritePatternHigh:            Array.from(this.spritePatternHigh),
            spriteAttributes:             Array.from(this.spriteAttributes),
            spriteXCounters:              Array.from(this.spriteXCounters),
            nextSpritePatternLow:         Array.from(this.nextSpritePatternLow),
            nextSpritePatternHigh:        Array.from(this.nextSpritePatternHigh),
            nextSpriteAttributes:         Array.from(this.nextSpriteAttributes),
            nextSpriteXCounters:          Array.from(this.nextSpriteXCounters),
        };
    }

    public deserialize(data: Record<string, any>): void
    {
        // Memory
        this.oamMemory.set(deserializeMemory(data.oamMemory));
        this.secondaryOam.set(deserializeMemory(data.secondaryOam));

        // Registers
        this.ctrlRegister.value       = data.ctrl;
        this.maskRegister.value       = data.mask;
        this.statusRegister.value     = data.status;
        this.oamAddress               = data.oamAddress;
        this._readBuffer              = data.readBuffer;

        // Loopy (Scroll) Registers
        this._vramAddress  = data.vramAddress;
        this._tempAddress  = data.tempAddress;
        this._fineXScroll  = data.fineXScroll;
        this._addressLatch = data.addressLatch;

        // Timing
        this._cycle    = data.cycle;
        this._scanline = data.scanline;
        this._frame    = data.frame;

        // Mid-scanline Latches (Arrays converted to standard JS arrays for JSON)
        this._spriteCount                  = data.spriteCount;
        this._nextSpriteCount              = data.nextSpriteCount;
        this._spriteZeroInSecondaryOam     = data.spriteZeroInSecondaryOam;
        this._nextSpriteZeroInSecondaryOam = data.nextSpriteZeroInSecondaryOam;

        this.spritePatternLow.set(data.spritePatternLow);
        this.spritePatternHigh.set(data.spritePatternHigh);
        this.spriteAttributes.set(data.spriteAttributes);
        this.spriteXCounters.set(data.spriteXCounters);
        this.nextSpritePatternLow.set(data.nextSpritePatternLow);
        this.nextSpritePatternHigh.set(data.nextSpritePatternHigh);
        this.nextSpriteAttributes.set(data.nextSpriteAttributes);
        this.nextSpriteXCounters.set(data.nextSpriteXCounters);
    }

    /**
     * Advances the PPU by a single cycle.
     */
    public tick(): void
    {
        this.renderPixel();
        this.swapSpriteBuffers();
        this.shiftRegisters();
        this.fetch();
        this.evaluateSprites();
        this.fetchSpriteData();
        this.updateFlags();
        this.tickScrollCounters();
        this.tickCounters();
    }

    /**
     * Peeks a PPU register at the given address.
     */
    public peekRegister(address: number): number
    {
        switch (address) {
            case 0x0002: /* PPU_STATUS */
                return this.statusRegister.value;
            case 0x0004: /* OAM_DATA */
                return this.oamMemory[this.oamAddress];
            case 0x0007: /* PPU DATA */
                return this.bus.read(this._vramAddress);
        }

        return 0;
    }

    /**
     * Reads a PPU register at the given address.
     */
    public readRegister(address: number): number
    {
        switch (address) {
            case 0x0002: { /* PPU_STATUS */
                const value: number = this.statusRegister.value;
                this.statusRegister.clearVBlank();
                this._addressLatch = false;

                if (value & 0x80) {
                    // This will print the exact function call stack that triggered this read!
                    // console.trace(`[GHOST CAUGHT] Someone read $2002 and cleared VBlank!`);
                }

                return value;
            }
            case 0x0004: /* OAM_DATA */
                return this.oamMemory[this.oamAddress];
            case 0x0007: { /* PPU_DATA */
                let data: number = this._readBuffer; // Return the delayed buffer
                this._readBuffer = this.bus.read(this._vramAddress); // Fill buffer with new data

                if (this._vramAddress >= 0x3F00) {
                    data = this.bus.read(this._vramAddress);
                }

                this._vramAddress += this.ctrlRegister.isIncrementAddress() ? 32 : 1;
                this._vramAddress &= 0x7FFF;

                return data;
            }
        }

        return 0;
    }

    /**
     * Writes to a PPU register at the given address.
     */
    public writeRegister(address: number, value: number): void
    {
        switch (address) {
            case 0x0000: { /* PPU_CTRL */
                this.ctrlRegister.value = value;
                this._tempAddress &= ~0xC00;
                this._tempAddress |= (value & 0x3) << 10;
                break;
            }
            case 0x0001: { /* PPU_MASK */
                this.maskRegister.value = value;
                break;
            }
            case 0x0003: { /* OAM_ADDR */
                this.oamAddress = value;
                break;
            }
            case 0x0004: { /* OAM_DATA */
                this.oamMemory[this.oamAddress] = value;
                this.oamAddress                 = (this.oamAddress + 1) & 0xFF;
                break;
            }
            case 0x0005: { /* PPU_SCROLL */
                if (!this._addressLatch) {
                    // First Write: X Scroll
                    this._fineXScroll = value & 0x7;
                    this._tempAddress &= ~0x001F;
                    this._tempAddress |= (value >> 3) & 0x1F;
                } else {
                    // Second Write: Y Scroll
                    this._tempAddress &= ~0x73E0;
                    this._tempAddress |= (value & 0xF8) << 2;
                    this._tempAddress |= (value & 0x7) << 12;
                }
                this._addressLatch = !this._addressLatch;
                break;
            }
            case 0x0006: { /* PPU_ADDR */
                if (!this._addressLatch) {
                    // First Write: High Byte
                    this._tempAddress &= 0x00FF; // Clear the high byte
                    this._tempAddress |= (value & 0x3F) << 8; // Set high byte (masked to 14 bits)
                } else {
                    // Second Write: Low Byte and Update VRAM
                    this._tempAddress &= 0xFF00; // Clear the low byte
                    this._tempAddress |= value & 0xFF; // Set low byte
                    this._vramAddress = this._tempAddress; // V = T
                }
                this._addressLatch = !this._addressLatch;
                break;
            }
            case 0x0007: { /* PPU_DATA */
                this.bus.write(this._vramAddress, value);
                this._vramAddress += this.ctrlRegister.isIncrementAddress() ? 32 : 1;
                this._vramAddress &= 0x7FFF;
                break;
            }
        }
    }

    /**
     * True if the PPU has a pending Non-Maskable Interrupt request.
     *
     * If true, the CPU's {@link CPU.scheduleNmi} method is invoked immediately
     * after the current invocation of {@link tick}.
     */
    public get isNmiRequested(): boolean
    {
        return this.statusRegister.getVBlank()
            && this.ctrlRegister.isNmiEnabled();
    }

    // region === Tick Methods ===

    /**
     * Advances the internal state counters (cycle, scanline and frame).
     */
    private tickCounters(): void
    {
        this._cycle++;

        if (this._cycle > 340) {
            this._cycle = 0;
            this._scanline++;

            if (this._scanline > 261) {
                this._scanline = 0;
                this._frame++;
            }
        }

        // TODO: Test if we really need this:
        if (this._scanline === 261 && this._cycle === 1) {
            this.statusRegister.clearVBlank();
            this.statusRegister.clearSpriteZeroHit();
            this.statusRegister.clearSpriteOverflowBit();
        }
    }

    /**
     * Advances the scroll counters.
     *
     * @private
     */
    private tickScrollCounters(): void
    {
        if (!this.maskRegister.isBackgroundVisible() && !this.maskRegister.isSpritesVisible()) {
            return; // Nothing to do.
        }

        if (this._scanline >= 240 && this._scanline <= 260) {
            return; // Nothing to do.
        }

        if (this._scanline === 261) {
            if (this._cycle >= 280 && this._cycle <= 304) {
                this._vramAddress &= ~0x7BE0;
                this._vramAddress |= (this._tempAddress & 0x7BE0);
            }
        }

        if (this._cycle === 0 || (this._cycle >= 258 && this._cycle <= 320)) {
            return; // Nothing further to do.
        }

        if ((this._cycle % 8) === 0) {
            let address: number = this._vramAddress;

            if ((address & 0x1F) === 31) {
                address &= ~0x1F;
                address ^= 0x400;
            } else {
                address++;
            }

            this._vramAddress = address;
        }

        if (this._cycle === 256) {
            let address: number = this._vramAddress;

            if ((address & 0x7000) !== 0x7000) {
                address += 0x1000;
            } else {
                address &= ~0x7000;
                let y: number = (address & 0x3E0) >> 5;

                if (y === 29) {
                    y = 0;
                    address ^= 0x800;
                } else if (y === 31) {
                    y = 0;
                } else {
                    y++;
                }

                address = (address & ~0x3E0) | (y << 5);
            }

            this._vramAddress = address;
        }

        if (this._cycle === 257) {
            this._vramAddress &= ~0x41F;
            this._vramAddress |= (this._tempAddress & 0x41F);
        }
    }

    private updateFlags(): void
    {
        if (this._cycle === 1) {
            if (this._scanline === 241) {
                this.statusRegister.setVBlank();
                this.dispatch('frame');
            } else if (this._scanline === 261) {
                this.statusRegister.clearVBlank();
                this.statusRegister.clearSpriteZeroHit();
                this.statusRegister.clearSpriteOverflowBit();
            }
        }
    }

    private fetch(): void
    {
        if (this._scanline >= 240 && this._scanline <= 260) {
            return;
        }

        if (!this.maskRegister.isBackgroundVisible() &&
            !this.maskRegister.isSpritesVisible()
        ) {
            return;
        }

        // Allow cycles 1-256, 321-336 (for memory reads), AND 257, 337 (for latch loading)
        if (this._cycle === 0 || (this._cycle >= 258 && this._cycle <= 320) || this._cycle >= 338) {
            return;
        }

        if ((this._cycle >= 1 && this._cycle <= 256) || (this._cycle >= 321 && this._cycle <= 336)) {
            switch ((this._cycle - 1) % 8) {
                case 0: /* Fetch nametable */
                    this.fetchNametable();
                    break;
                case 2: /* Fetch attribute table */
                    this.fetchAttributeTable();
                    break;
                case 4: /* Fetch pattern table low */
                    this.fetchPatternTableLow();
                    break;
                case 6: /* Fetch pattern table high */
                    this.fetchPatternTableHigh();
                    break;
            }
        }

        if (this._cycle % 8 === 1) {
            this.nametableRegister.value        = this._nametableLatch;
            this.attributeTableLowRegister.low  = this._attributeTableLowLatch;
            this.attributeTableHighRegister.low = this._attributeTableHighLatch;
            this.patternTableLowRegister.low    = this._patternTableLowLatch;
            this.patternTableHighRegister.low   = this._patternTableHighLatch;
        }
    }

    private shiftRegisters(): void
    {
        if (this.maskRegister.isBackgroundVisible() && (
            (this._cycle >= 1 && this._cycle <= 256) ||
            (this._cycle >= 321 && this._cycle <= 337)
        )) {
            this.patternTableLowRegister.value <<= 1;
            this.patternTableHighRegister.value <<= 1;
            this.attributeTableLowRegister.value <<= 1;
            this.attributeTableHighRegister.value <<= 1;
        }
    }

    // endregion

    // region === Fetch Operations ===

    private fetchNametable(): void
    {
        this._nametableLatch = this.bus.read(0x2000 | (this._vramAddress & 0x0FFF));
    }

    private fetchAttributeTable(): void
    {
        const va: number        = this._vramAddress;
        const address: number   = 0x23C0 | (va & 0x0C00) | ((va >> 4) & 0x38) | ((va >> 2) & 0x07);
        const byte: number      = this.bus.read(address);
        const coarseX: number   = va & 0x1F;
        const coarseY: number   = (va >> 5) & 0x1F;
        const topBottom: number = (coarseY % 4) >= 2 ? 1 : 0;
        const rightLeft: number = (coarseX % 4) >= 2 ? 1 : 0;
        const position: number  = (topBottom << 1) | rightLeft;
        const value: number     = (byte >> (position << 1) & 0x3);

        this._attributeTableHighLatch = (value >> 1) === 1 ? 0xFF : 0;
        this._attributeTableLowLatch  = (value & 1) === 1 ? 0xFF : 0;
    }

    private fetchPatternTableLow(): void
    {
        this._patternTableLowLatch = this.bus.read(this.fetchPatternTableAddress());
    }

    private fetchPatternTableHigh(): void
    {
        this._patternTableHighLatch = this.bus.read(this.fetchPatternTableAddress() + 0x8);
    }

    private fetchPatternTableAddress(): number
    {
        return this.ctrlRegister.getBackgroundPatternTable()
            * 0x1000 + this.nametableRegister.value
            * 0x10 + ((this._vramAddress >> 12) & 0x07);
    }

    // endregion

    // region === Sprite Operations ===

    private evaluateSprites(): void
    {
        if (this._scanline >= 240 || this._cycle !== 240) {
            return;
        }

        this.secondaryOam.fill(0xFF);
        this._nextSpriteCount              = 0;
        this._nextSpriteZeroInSecondaryOam = false;

        const spriteHeight: number = this.ctrlRegister.isSpriteSize16() ? 16 : 8;

        for (let i = 0; i < 64; i++) {
            const oamIndex: number = i * 4;
            const spriteY: number  = this.oamMemory[oamIndex];
            const row: number      = this._scanline - spriteY;

            if (row >= 0 && row < spriteHeight) {
                if (this._nextSpriteCount < 8) {
                    const secondaryIndex: number          = this._nextSpriteCount * 4;
                    this.secondaryOam[secondaryIndex]     = this.oamMemory[oamIndex];
                    this.secondaryOam[secondaryIndex + 1] = this.oamMemory[oamIndex + 1];
                    this.secondaryOam[secondaryIndex + 2] = this.oamMemory[oamIndex + 2];
                    this.secondaryOam[secondaryIndex + 3] = this.oamMemory[oamIndex + 3];

                    if (i === 0) {
                        this._nextSpriteZeroInSecondaryOam = true;
                    }

                    this._nextSpriteCount++;
                } else {
                    this.statusRegister.setSpriteOverflowBit();
                    break;
                }
            }
        }
    }

    private fetchSpriteData(): void
    {
        if (this._scanline >= 240 || this._cycle !== 240) {
            return;
        }

        if (!this.maskRegister.isBackgroundVisible() && !this.maskRegister.isSpritesVisible()) {
            return;
        }

        const is8x16: boolean = this.ctrlRegister.isSpriteSize16();

        for (let i = 0; i < 8; i++) {
            if (i >= this._nextSpriteCount) {
                this.nextSpritePatternLow[i]  = 0;
                this.nextSpritePatternHigh[i] = 0;
                this.nextSpriteAttributes[i]  = 0;
                this.nextSpriteXCounters[i]   = 0;

                const patternTable: number = is8x16 ? 0x1000 : (this.ctrlRegister.getSpritePatternTable() * 0x1000);
                const dummyAddress: number = patternTable | 0x0FF0;

                this.bus.read(dummyAddress);
                this.bus.read(dummyAddress + 8);
                continue;
            }

            const secondaryIndex: number = i * 4;

            const y: number          = this.secondaryOam[secondaryIndex];
            let tile: number         = this.secondaryOam[secondaryIndex + 1];
            const attributes: number = this.secondaryOam[secondaryIndex + 2];
            const x: number          = this.secondaryOam[secondaryIndex + 3];

            const isFlippedHorizontally: boolean = (attributes & 0x40) !== 0;
            const isFlippedVertically: boolean   = (attributes & 0x80) !== 0;

            let spriteRow: number      = this._scanline - y;
            let patternAddress: number = 0;

            if (!is8x16) {
                if (isFlippedVertically) {
                    spriteRow = 7 - spriteRow;
                }

                patternAddress = (this.ctrlRegister.getSpritePatternTable() * 0x1000) + (tile * 16) + spriteRow;
            } else {
                const tableBank: number = tile & 1;          // Bit 0 is the table ($0000 or $1000)
                let tileIndex: number   = tile & 0xFE;       // Bits 1-7 are the actual tile index

                if (isFlippedVertically) {
                    spriteRow = 15 - spriteRow;
                }

                if (spriteRow >= 8) {
                    tileIndex++; // Increment the pure index, safely ignoring the bank bit
                    spriteRow -= 8;
                }

                patternAddress = (tableBank * 0x1000) + (tileIndex * 16) + spriteRow;
            }

            let patternLow: number  = this.bus.read(patternAddress);
            let patternHigh: number = this.bus.read(patternAddress + 8);

            if (isFlippedHorizontally) {
                patternLow  = this.flipByte(patternLow);
                patternHigh = this.flipByte(patternHigh);
            }

            this.nextSpritePatternLow[i]  = patternLow;
            this.nextSpritePatternHigh[i] = patternHigh;
            this.nextSpriteAttributes[i]  = attributes;
            this.nextSpriteXCounters[i]   = x;
        }
    }

    /**
     * Reverses the bits in an 8-bit number (e.g., 11000000 -> 00000011).
     */
    private flipByte(b: number): number
    {
        b = ((b & 0xF0) >> 4) | ((b & 0x0F) << 4);
        b = ((b & 0xCC) >> 2) | ((b & 0x33) << 2);
        b = ((b & 0xAA) >> 1) | ((b & 0x55) << 1);

        return b;
    }

    // region === Rendering Operations ===

    private renderPixel(): void
    {
        if (this._scanline >= 240 || this._cycle === 0 || this._cycle > 256) {
            return;
        }

        const x: number = this._cycle - 1;
        const y: number = this._scanline;

        // --- Background Pixel ---
        let bgPixel: number   = 0;
        let bgPalette: number = 0;

        if (this.maskRegister.isBackgroundVisible()) {
            const mask: number = 0x8000 >> this._fineXScroll;
            const p0: number   = (this.patternTableLowRegister.value & mask) !== 0 ? 1 : 0;
            const p1: number   = (this.patternTableHighRegister.value & mask) !== 0 ? 1 : 0;
            const a0: number   = (this.attributeTableLowRegister.value & mask) !== 0 ? 1 : 0;
            const a1: number   = (this.attributeTableHighRegister.value & mask) !== 0 ? 1 : 0;

            bgPixel   = (p1 << 1) | p0;
            bgPalette = (a1 << 1) | a0;
        }

        // --- Sprite Pixel ---
        let spritePixel: number    = 0;
        let spritePalette: number  = 0;
        let spritePriority: number = 0; // 0 = Front, 1 = Back
        let isSpriteZero: boolean  = false;

        if (this.maskRegister.isSpritesVisible()) {
            for (let i = 0; i < this._spriteCount; i++) {
                // Calculate if the current screen X coordinate is inside this 8-pixel sprite
                const offset: number = x - this.spriteXCounters[i];

                if (offset >= 0 && offset < 8) {
                    // Extract the specific bit for this pixel
                    // Since we already flipped horizontally during fetch, we always read left-to-right
                    const colorBit: number = 7 - offset;
                    const p0: number       = (this.spritePatternLow[i] >> colorBit) & 1;
                    const p1: number       = (this.spritePatternHigh[i] >> colorBit) & 1;

                    const pixel: number = (p1 << 1) | p0;

                    // Is this pixel opaque?
                    if (pixel !== 0) {
                        spritePixel    = pixel;
                        spritePalette  = (this.spriteAttributes[i] & 0x03) + 4; // Sprites use palettes 4-7
                        spritePriority = (this.spriteAttributes[i] & 0x20) === 0 ? 0 : 1;

                        if (i === 0 && this._spriteZeroInSecondaryOam) {
                            isSpriteZero = true;
                        }

                        break; // We found the highest priority opaque sprite, stop looking!
                    }
                }
            }
        }

        // --- Multiplexer & Sprite 0 Hit ---
        let finalPixel: number;
        let finalPalette: number;

        if (bgPixel === 0 && spritePixel === 0) {
            // Both transparent: Draw universal background
            finalPixel   = 0;
            finalPalette = 0;
        } else if (bgPixel === 0 && spritePixel > 0) {
            // BG transparent, Sprite opaque: Draw Sprite
            finalPixel   = spritePixel;
            finalPalette = spritePalette;
        } else if (bgPixel > 0 && spritePixel === 0) {
            // BG opaque, Sprite transparent: Draw BG
            finalPixel   = bgPixel;
            finalPalette = bgPalette;
        } else {
            // Both opaque: Collision!

            // Trigger Sprite 0 Hit if conditions are met
            if (isSpriteZero) {
                // Note: The real NES has a few edge cases where Sprite 0
                //       doesn't trigger at x=255, but this logic is robust
                //       enough for most games.
                this.statusRegister.setSpriteZeroHit();
            }

            // Resolve sprite vs BG priority
            if (spritePriority === 0) {
                finalPixel   = spritePixel;
                finalPalette = spritePalette;
            } else {
                finalPixel   = bgPixel;
                finalPalette = bgPalette;
            }
        }

        // --- Draw to Framebuffer ---
        let paletteAddress: number = 0x3F00;
        if (finalPixel !== 0) {
            paletteAddress += (finalPalette << 2) | finalPixel;
        }

        this.frameBuffer[y * 256 + x] = NesPalette[this.bus.read(paletteAddress) & 0x3F];
    }

    private swapSpriteBuffers(): void
    {
        if (this._cycle === 256) {
            for (let i = 0; i < 8; i++) {
                this.spritePatternLow[i]  = this.nextSpritePatternLow[i];
                this.spritePatternHigh[i] = this.nextSpritePatternHigh[i];
                this.spriteAttributes[i]  = this.nextSpriteAttributes[i];
                this.spriteXCounters[i]   = this.nextSpriteXCounters[i];
            }
            this._spriteCount              = this._nextSpriteCount;
            this._spriteZeroInSecondaryOam = this._nextSpriteZeroInSecondaryOam;
        }
    }

    // endregion
}

export interface IPPUEvents
{
    /**
     * Invoked when the PPU has rendered a full frame.
     */
    frame: [];
}
